import * as React from 'react';
import {AcceptIcon, Button, ChevronEndIcon, ChevronStartIcon, CloseIcon, Flex, List, Loader, Text} from '@fluentui/react-northstar';
import Overflow from '../OverFlow/sentMessageOverflow';
import './messages.scss';
import {languageObject} from '../../translations';
import {GenericDialog} from "../Dialogs/GenericDialog";
import StatusTaskModule from "../StatusTaskModule/statusTaskModule";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {ISentMessageSummary} from "../../Interfaces/ISentMessageSummary";
import {IMessageChannel, IMessageState} from "../../Interfaces/Message";
import moment from "moment";
import {getSentNotificationsSummaries} from "../../apis/messageListApi";
import {IDraftMessageSummary} from "../../Interfaces/IDraftMessageSummary";

export interface IMessagesProps {
    channelId: string;
    upnContext: string;
    numberOfSent: (count: number) => void;
    onRef: (ref: IMessagesRef) => void;
    onDuplicateMessage: () => void;
    onResendMessage: () => void;
    onMessagesFetch: (messages: IDraftMessageSummary[]) => void;
    filteredAuthors: string[];
    filteredGPTs: string[];
}

export interface IMessagesState {
    messages: ISentMessageSummary[];
    loaded: boolean;
    showStatusDialog: boolean;
    messageId: string;
    page: number;
    pageSize: number;
    totalCount: number;
}

export interface IMessagesRef {
    messages: ISentMessageSummary[];
    refreshMessages: () => void;
}

class Messages extends React.Component<IMessagesProps, IMessagesState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    private _isMounted: boolean = false;
    private interval: NodeJS.Timeout | undefined = undefined;

    private columnsWidth = {
        title: "30%",
        status: "12%",
        recipients: "12%",
        sentDate: "12%",
        channel: "14%",
        author: "20%",
    }

    constructor(props: IMessagesProps) {
        super(props);
        this.state = {
            messages: new Array<ISentMessageSummary>(),
            loaded: false,
            showStatusDialog: false,
            messageId: "",
            page: 1,
            pageSize: 10,
            totalCount: 0,
        };
        this.escFunction = this.escFunction.bind(this);
        this.refreshMessages = this.refreshMessages.bind(this);
        this.handleHideStatusDialog = this.handleHideStatusDialog.bind(this);
    }

    public async componentDidMount() {
        this._isMounted = true;
        await this.refreshMessages();
        document.addEventListener("keydown", this.escFunction, false);
        this.interval = setInterval(async () => {
            await this.refreshMessages();
        }, 30000);
    }

    public componentDidUpdate(prevProps: IMessagesProps, prevState: IMessagesState) {
        if (this.state.totalCount != prevState.totalCount) {
            this.props.numberOfSent(this.state.totalCount)
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
        document.removeEventListener("keydown", this.escFunction, false);
        if (!this.interval) return;
        clearInterval(this.interval);
    }

    shouldComponentUpdate(nextProps: Readonly<IMessagesProps>, nextState: Readonly<IMessagesState>): boolean {
        return this.state.loaded || nextState.loaded;
    }

    async refreshMessages() {
        const response = await getSentNotificationsSummaries(this.state.page, this.state.pageSize);
        if (this._isMounted) this.setState({
            messages: response.items,
            totalCount: response.totalCount,
            loaded: true
        });
        this.props.numberOfSent(response.totalCount);
        this.props.onRef({
            messages: response.items,
            refreshMessages: this.refreshMessages
        });
        this.props.onMessagesFetch(response.items);
    }

    private handleHideStatusDialog() {
        if (!this._isMounted) return;
        this.setState({showStatusDialog: false, messageId: ""});
    }

    renderViewStatusDialog() {
        return (
            <GenericDialog
                title={this.state.messages.find(m => m.id === this.state.messageId)?.title}
                width={1000} height={700} show={this.state.showStatusDialog}
                onHide={this.handleHideStatusDialog}>
                <StatusTaskModule messageId={this.state.messageId} {...this.props} />
            </GenericDialog>
        )
    }

    handleShowMessage(message: ISentMessageSummary) {
        if (message.state !== IMessageState.Sent) return;
        this.setState({messageId: message.id, showStatusDialog: true});
    }

    public getFilteredMessages = () => {
        return this.state.messages.filter(message => {
            if (this.props.filteredAuthors.length > 0 && !this.props.filteredAuthors.includes(message.authorName)) {
                return false;
            }
            if (this.props.filteredGPTs.length > 0 && message.gptId && !this.props.filteredGPTs.includes(message.gptId)) {
                return false;
            }
            return true;
        });
    }

    public render(): JSX.Element {
        if (!this.state.loaded) return (
            <Flex>
                <Loader size={"small"} labelPosition={"end"} label={languageObject.translate("LoadingMessages")}/>
            </Flex>
        );

        let keyCount = 0;
        const processItem = (message: ISentMessageSummary) => {
            keyCount++;
            return {
                key: keyCount,
                content: this.messageContent(message),
                className: "message-list-item",
                onClick: (e: any): void => {
                    if (!e.defaultPrevented && this._isMounted) this.handleShowMessage(message);
                },
                styles: {padding: "0 16px", margin: 0},
            };
        };

        const label = this.processLabels();
        const outList = this.getFilteredMessages().map(processItem);
        const allMessages = [...label, ...outList];

        const hasPrev = this.state.page > 1;
        const hasNext = this.state.page * this.state.pageSize < this.state.totalCount;

        return <>
            {this.renderViewStatusDialog()}
            {this.state.totalCount === 0 ?
                <div className="results">{languageObject.translate("EmptySentMessages")}</div>
                :
                <>
                    <List selectable items={allMessages} className="list"/>
                    <Flex hAlign="center" vAlign="center" gap="gap.small" styles={{padding: "8px 0"}}>
                        <Button
                            icon={<ChevronStartIcon/>}
                            text
                            disabled={!hasPrev}
                            content={languageObject.translate("Previous")}
                            onClick={() => this.goToPage(this.state.page - 1)}
                        />
                        <Text content={`${this.state.page} / ${Math.ceil(this.state.totalCount / this.state.pageSize)}`} styles={{lineHeight: "32px"}}/>
                        <Button
                            icon={<ChevronEndIcon/>}
                            text
                            iconPosition="after"
                            disabled={!hasNext}
                            content={languageObject.translate("Next")}
                            onClick={() => this.goToPage(this.state.page + 1)}
                        />
                    </Flex>
                </>
            }
        </>
    }

    private goToPage = async (newPage: number) => {
        if (!this._isMounted) return;
        this.setState({page: newPage, loaded: false}, async () => {
            await this.refreshMessages();
        });
    }

    private showStatusDialog = (id: string) => {
        if (!this._isMounted) return;
        this.setState({messageId: id, showStatusDialog: true});
    }

    private processLabels = () => {
        return [{
            key: "labels",
            content: (
                <Flex fill gap="gap.medium" vAlign={"center"}>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.title}}>
                        <Text
                            truncated
                            weight="bold"
                            content={languageObject.translate("TitleText")}
                        />
                    </Flex>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.status}}>
                        <Text
                            truncated
                            weight="bold"
                            content={languageObject.translate("State")}
                        />
                    </Flex>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.recipients}}>
                        <Text
                            truncated
                            content={languageObject.translate("Recipients")}
                            weight="bold"
                        />
                    </Flex>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.sentDate}}>
                        <Text
                            truncated
                            content={languageObject.translate("SentDate")}
                            weight="bold"
                        />
                    </Flex>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.channel}}>
                        <Text
                            truncated
                            content={languageObject.translate("Channel")}
                            weight="bold"
                        />
                    </Flex>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.author}}>
                        <Text
                            truncated
                            content={languageObject.translate("Author")}
                            weight="bold"
                        />
                    </Flex>
                    <Flex styles={{minWidth: "32px", maxWidth: "32px"}}/>
                </Flex>
            ),
            styles: {padding: "0 16px"},
        }];
    }

    private renderSendingText = (message: ISentMessageSummary) => {
        let text = "";
        switch (message.state) {
            case IMessageState.Ready:
                text = languageObject.translate("Ready");
                break;
            case IMessageState.Sending:
                let sentCount =
                    (message.numberOfSuccess ? message.numberOfSuccess : 0) +
                    (message.numberOfFailure ? message.numberOfFailure : 0);
                text = languageObject.translate("SendingMessages", {
                    "SentCount": sentCount,
                    "TotalCount": message.numberToSend ?? "?"
                });
                break;
            case IMessageState.Sent:
                let textId = "Sent";
                //if (message.numberOfSuccess + message.numberOfFailure === 0) textId = "FailedToSend";
                text = languageObject.translate(textId);
                break;
            case IMessageState.PrepareResending:
                text = languageObject.translate("PrepareResending");
                break;
            case IMessageState.Planned:
                text = languageObject.translate("Planned");
                break;
        }

        return (<Text truncated content={text} title={text}/>);
    }

    private renderSendingDate = (message: ISentMessageSummary) => {
        if (message.state === IMessageState.Planned) {
            return !message.delayedSendDate ? "" : moment(message.delayedSendDate).format("lll");
        }
        return !message.sendingStart ? "" : moment(message.sendingStart).format("lll");
    }

    private messageContent = (message: ISentMessageSummary) => {
        const isGpt = message.channel === IMessageChannel.Channel_GPTPro;
        const channelName = languageObject.translate(IMessageChannel[message.channel]);
        const gptName = isGpt ? this.context.gpts.list.find(g => g.id === message.gptId)?.shortName : "";
        return (
            <Flex vAlign="center" fill gap="gap.medium">
                <Flex fill vAlign={"center"} className={"overflow-hidden"} styles={{width: this.columnsWidth.title}}>
                    <Text truncated content={message.title} title={message.title}/>
                </Flex>
                <Flex fill vAlign={"center"} className={"overflow-hidden"} styles={{width: this.columnsWidth.status}}>
                    {this.renderSendingText(message)}
                </Flex>
                <Flex fill vAlign={"center"} className={"overflow-hidden"}
                      styles={{width: this.columnsWidth.recipients}}>
                    <Flex gap={"gap.small"} className={"overflow-hidden"}>
                        <Flex gap={"gap.smaller"} className={"overflow-hidden"} vAlign={"center"}>
                            <AcceptIcon size={"small"} xSpacing="after" className="succeeded" outline/>
                            <Text
                                truncated content={message.numberOfSuccess}
                                title={message.numberOfSuccess + ""}
                                styles={{marginBottom: "2px"}}
                            />
                        </Flex>
                        <Flex gap={"gap.smaller"} className={"overflow-hidden"} vAlign={"center"}>
                            <CloseIcon size={"small"} xSpacing="both" className="failed" outline/>
                            <Text
                                truncated content={message.numberOfFailure}
                                title={message.numberOfFailure + ""}
                                styles={{marginBottom: "2px"}}
                            />
                        </Flex>
                    </Flex>
                </Flex>
                <Flex fill vAlign={"center"} className={"overflow-hidden"} styles={{width: this.columnsWidth.sentDate}}>
                    <Text
                        truncated
                        content={this.renderSendingDate(message)}
                    />
                </Flex>
                <Flex fill vAlign={"center"} className={"overflow-hidden"} styles={{width: this.columnsWidth.channel}}>
                    <Text
                        truncated
                        content={
                            languageObject.translate(IMessageChannel[message.channel]) +
                            (isGpt && gptName !== channelName && gptName !== undefined ? ` (${gptName})` : '')
                        }
                    />
                </Flex>
                <Flex fill vAlign={"center"} className={"overflow-hidden"} styles={{width: this.columnsWidth.author}}>
                    <Text
                        truncated
                        content={message.authorName}
                    />
                </Flex>
                <Flex vAlign={"center"} styles={{minWidth: "32px"}}>
                    <Overflow
                        message={message}
                        showStatusDialog={this.showStatusDialog}
                        upnContext={this.props.upnContext}
                        channelId={this.props.channelId}
                        onDuplicateMessage={this.props.onDuplicateMessage}
                        onResendMessage={this.props.onResendMessage}
                    />
                </Flex>
            </Flex>
        );
    }

    private readonly escFunction = (event: any) => {
        if (!this._isMounted) return;
        if (event.keyCode === 27 || (event.key === "Escape")) {
            this.setState({showStatusDialog: false, messageId: ""});
        }
    }
}

export default Messages;